import { useTranslation } from 'react-i18next';
import { WALL_TYPE } from '@cx-sdk/constants';
import { type SubscriptionTerm } from '@cx-sdk/subs';
import { SUBSCRIPTION_TERM } from '@cx-sdk/subs/schema';
import { Card } from '@fpsm/cxy-components/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@fpsm/cxy-components/Tabs';
import { type SerializeFrom } from '@remix-run/node';
import { useLoaderData, useMatches } from '@remix-run/react';
import clsx from 'clsx';

import Authentication from '~/apps/product-plus/components/Authentication';
import {
  Dialog as AuthDialog,
  DialogContent,
  DialogTitle } from
'~/apps/product-plus/components/Authentication/components/Dialog';
import { trackLandingPagePlansToggleClick } from '~/apps/product-plus/components/BettingInsights/tracking';
import FAQ from '~/apps/product-plus/components/FAQ';
import { navigateToStripeManagement } from '~/apps/product-plus/components/Subscribe/helpers/navigateToStripeManagement';
import SubscriptionCard from '~/apps/product-plus/components/Subscribe/SubscriptionCard';
import { useSubscriptionFlow } from '~/apps/product-plus/hooks/use-subscription-flow.ts';
import { useTrackCTAImpression } from '~/apps/product-plus/hooks/use-track-cta-impression.ts';
import { createPositiveEvConfig } from '~/apps/product-plus/pages/config';
import { useAuthModal, VIEW_VERIFY_PAYMENT } from '~/apps/product-plus/providers/auth-modal-provider';
import { useMyOCUser } from '~/providers/myoc-user';
import { STRIPE_PRODUCT_IDS } from '~/repub-config';
import { type RequestContext } from '~/types/RequestContext';
import { type SubsProduct } from '~/types/subs-product';
import { getDefaultProduct } from '~/utils/getDefaultProduct';
import { SubscriptionType, userHasSubscriptionType } from '~/utils/getUserSubscriptionTypes';

import type { loader as rootLoader } from '../../../../root-loader';
import { useSubscriptionRedirect } from '../shared/hooks/useSubscriptionRedirect';
import { getPostLoginSubscribeProduct } from '../shared/utils/getPostLoginSubscribeProduct';
import landingPageStyles from '../styles/landing-page.module.css';

import styles from './styles/subscribe.module.css';
import { ESSENTIAL_FEATURES, FAQ_ITEMS, PREMIUM_FEATURES } from './constants';
import { type LoaderDataTwo } from './loader';
export { meta } from './meta';
export { loader } from './loader';

export default function TwoTieredLandingPage() {
  const matches = useMatches();
  const rootMatch = matches.find((match) => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  const { t } = useTranslation(['label']);
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const {
    cancelPayment,
    hasMonthlyAndYearlyOptions,
    hasMonthlyOptions,
    isLoggedIn,
    isSubscribed,
    isUpgrade,
    monthlyProducts,
    productPrice,
    redirectPath,
    repub,
    requestContext,
    subscriptionStatus,
    subscriptionTerm,
    subscriptionTypes,
    verifyPayment,
    yearlyProducts
  } = useLoaderData<LoaderDataTwo>();

  const { repubPath, resourcesUrl, socialLoginIds, url: currentUrl } = rootLoaderData;

  const { myocData } = useMyOCUser();
  const { isOpen, toggle, view } = useAuthModal();

  const defaultMonthlyProduct = getDefaultProduct(monthlyProducts);
  const availableProducts = [...monthlyProducts, ...yearlyProducts];

  const { searchParams, shouldRedirectToPaymentPortal } = useSubscriptionRedirect(isSubscribed, cancelPayment);

  const config = createPositiveEvConfig(requestContext as RequestContext);
  const isPaywalled = config.isWalled && config.wallType === WALL_TYPE.PAYWALL;
  const isVerifyingPayment = view === VIEW_VERIFY_PAYMENT;

  const hasMonthlyEssential = userHasSubscriptionType(subscriptionTypes, SubscriptionType.MONTHLY_ESSENTIAL);
  const hasYearlyEssential = userHasSubscriptionType(subscriptionTypes, SubscriptionType.YEARLY_ESSENTIAL);

  const canUpgrade = isSubscribed && (hasMonthlyEssential || hasYearlyEssential);

  // Use shared subscription flow hook with upgrade customization
  const { handleProductClick, userHasCancelledSubscription } = useSubscriptionFlow(
    {
      canUpgrade,
      cancelPayment,
      isLoggedIn,
      isSubscribed,
      productPrice,
      redirectPath,
      repub,
      repubPath,
      requestContext,
      subscriptionTerm: (subscriptionTerm ?? undefined) as SubscriptionTerm | undefined,
      userId: myocData?.myOCUser?.userID,
      verifyPayment
    },
    {
      availableProducts,
      getSelectionLabel: (product) => product.name as 'Essential' | 'Premium',
      onBeforeSubscribe: (product) => {
        // Handle upgrade flow for essential subscribers
        if (subscriptionStatus.hasEssentialSubscription) {
          navigateToStripeManagement({
            isUpgrade: hasMonthlyEssential || hasYearlyEssential,
            repub,
            upgradeProduct: product
          });
          return false; // Stop normal flow
        }
        return true; // Continue normal flow
      },
      onPostLoginSubscribe: () =>
      getPostLoginSubscribeProduct(
        shouldRedirectToPaymentPortal,
        searchParams,
        availableProducts,
        defaultMonthlyProduct
      )
    }
  );

  const ref = useTrackCTAImpression({
    wallType: isUpgrade ? 'plan_upgrade_cta' : 'landing_page_cta',
    title: isUpgrade ? 'upgrade_component' : 'paywall_component'
  });

  const renderSubscriptionCards = (products: SubsProduct[], hasEssential: boolean) => {
    const premiumProduct = products.find(
      (p) => p.productId === STRIPE_PRODUCT_IDS.PREMIUM_MONTHLY || p.productId === STRIPE_PRODUCT_IDS.PREMIUM_ANNUALLY
    );
    const essentialProduct = products.find(
      (p) =>
      p.productId === STRIPE_PRODUCT_IDS.ESSENTIAL_MONTHLY || p.productId === STRIPE_PRODUCT_IDS.ESSENTIAL_ANNUALLY
    );

    return (
      <SubscriptionCard.Container>
        {premiumProduct &&
        <SubscriptionCard
          featured
          features={PREMIUM_FEATURES}
          handleSubscribe={(args) => {
            handleProductClick(args);
          }}
          isEssentialSubscriber={hasMonthlyEssential || hasYearlyEssential}
          isLoggedIn={isLoggedIn}
          pricingLabel="Less than £1 per day"
          product={premiumProduct}
          title="PREMIUM"
          userHasCancelledSubscription={userHasCancelledSubscription}
          variant="premium" />

        }
        {!hasEssential && essentialProduct &&
        <SubscriptionCard
          features={ESSENTIAL_FEATURES}
          handleSubscribe={(args) => {
            handleProductClick(args);
          }}
          isLoggedIn={isLoggedIn}
          pricingLabel="Less than 35p per day"
          product={essentialProduct}
          title="ESSENTIAL"
          userHasCancelledSubscription={userHasCancelledSubscription}
          variant="essential" />

        }
      </SubscriptionCard.Container>);

  };

  let SubscriptionCards = hasMonthlyAndYearlyOptions ?
  <Tabs defaultValue="monthly">
      <TabsList className={styles.tabsList} variant="block">
        <TabsTrigger
        onClick={() => {
          trackLandingPagePlansToggleClick({
            selection: SUBSCRIPTION_TERM.MONTHLY
          });
        }}
        value="monthly"
        variant="block">

          Monthly
        </TabsTrigger>
        <TabsTrigger
        onClick={() => {
          trackLandingPagePlansToggleClick({
            selection: 'annual'
          });
        }}
        value="annual"
        variant="block">

          Annual
        </TabsTrigger>
      </TabsList>
      <TabsContent value="monthly">{renderSubscriptionCards(monthlyProducts, hasMonthlyEssential)}</TabsContent>
      <TabsContent value="annual">{renderSubscriptionCards(yearlyProducts, hasYearlyEssential)}</TabsContent>
    </Tabs> :
  hasMonthlyOptions ?
  renderSubscriptionCards(monthlyProducts, hasMonthlyEssential) :

  renderSubscriptionCards(yearlyProducts, hasYearlyEssential);


  return (
    <>
      <div className={landingPageStyles.gradient} />

      <main className={clsx(landingPageStyles.home, landingPageStyles['home--narrow'], landingPageStyles['home--uk'])}>
        <div className={landingPageStyles.hero} data-testid="hero-content" ref={ref}>
          <div className={landingPageStyles.heroContent}>
            <div
              className={clsx(
                landingPageStyles.textContainer,
                landingPageStyles['textContainer--narrow'],
                styles.textContainer
              )}>

              <h1 className={landingPageStyles.title}>
                {hasMonthlyEssential ? 'Upgrade to Oddschecker+ for under £1 per day' : 'GET OC+ FREE FOR 7 DAYS'}
              </h1>
              <h2 className={landingPageStyles.subtitle}>{t('CX_PLUS_LANDING_TWO_TIER_SUBTITLE')}</h2>
            </div>
          </div>
          <picture className={landingPageStyles.heroImage}>
            <img alt="" data-testid="hero-image" src={`${resourcesUrl}/assets/img/subscribe/hero-v2.png`} />
          </picture>
        </div>

        <Card className={styles.subscriptionCardContainer} variant="secondary">
          {SubscriptionCards}
        </Card>

        <FAQ>
          <FAQ.Title>FAQs</FAQ.Title>
          <FAQ.Subtitle>Discover answers to frequently asked questions about oddschecker+</FAQ.Subtitle>
          <FAQ.List items={FAQ_ITEMS} />
        </FAQ>

        <AuthDialog onOpenChange={toggle} open={isOpen}>
          <DialogContent aria-describedby={undefined}>
            <DialogTitle style={{ display: 'none' }}>Log in or Sign up</DialogTitle>
            <Authentication>
              <Authentication.InfoSection resourcesUrl={resourcesUrl} view={view}>
                {isPaywalled && !isVerifyingPayment && <Authentication.InfoSection.InfoLink />}
              </Authentication.InfoSection>
              <Authentication.AuthSection
                currentUrl={currentUrl}
                facebookID={socialLoginIds?.facebookID ?? ''}
                googleID={socialLoginIds?.googleID ?? ''}
                view={view} />

            </Authentication>
          </DialogContent>
        </AuthDialog>
      </main>
    </>);

}